import type { CommerceClient, StoreWithOpeningHours } from '@altazion/commerce-sdk-core'

import {
  requireNumber,
  requireString,
  type AltazionModuleDefinition,
  type AltazionSuccessEventKind,
} from '../altazion-engine'

export type AltazionStoresActionName = 'findByLocation' | 'findByPostalCode' | 'getStore'

export type StoresActionResult = StoreWithOpeningHours | StoreWithOpeningHours[]

export const storesModuleDefinition: AltazionModuleDefinition<'stores', AltazionStoresActionName, StoresActionResult> = {
  moduleName: 'stores',
  actionAttribute: 'hx-altazion-stores-action',
  payloadAttributes: {
    'hx-altazion-stores-store-guid': 'storeGuid',
    'hx-altazion-stores-latitude': 'latitude',
    'hx-altazion-stores-longitude': 'longitude',
    'hx-altazion-stores-radius-km': 'radiusKm',
    'hx-altazion-stores-postal-code': 'postalCode',
    'hx-altazion-stores-country-code': 'countryCode',
  },
  payloadAliases: {
    lat: 'latitude',
    lon: 'longitude',
    lng: 'longitude',
    radius: 'radiusKm',
    storeId: 'storeGuid',
    id: 'storeGuid',
    postal: 'postalCode',
    country: 'countryCode',
  },
  parseAction(actionValue: string): AltazionStoresActionName {
    switch (actionValue) {
      case 'findByLocation':
      case 'findByPostalCode':
      case 'getStore':
        return actionValue
      default:
        throw new Error(`Unsupported stores action: ${actionValue}`)
    }
  },
  execute(client: CommerceClient, action: AltazionStoresActionName, payload: Record<string, unknown>): Promise<StoresActionResult> {
    switch (action) {
      case 'findByLocation': {
        const latitude = requireNumber(payload.latitude, 'latitude')
        const longitude = requireNumber(payload.longitude, 'longitude')
        const countryCode = payload.countryCode === undefined
          ? 'FRA'
          : requireString(payload.countryCode, 'countryCode')
        return client.stores.findByLocation(latitude, longitude, countryCode)
      }
      case 'findByPostalCode': {
        const postalCode = requireString(payload.postalCode, 'postalCode')
        const countryCode = payload.countryCode === undefined
          ? 'FR'
          : requireString(payload.countryCode, 'countryCode')
        return client.stores.findByPostalCode(postalCode, countryCode)
      }
      case 'getStore':
        return client.stores.getStore(requireString(payload.storeGuid, 'storeGuid'))
    }
  },
  successEventKind(): AltazionSuccessEventKind {
    return 'loaded'
  },
  getResultEventSuffixes(action: AltazionStoresActionName): string[] {
    switch (action) {
      case 'getStore':
        return ['store-loaded']
      case 'findByLocation':
      case 'findByPostalCode':
        return ['stores-loaded']
    }
  },
}